import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AdminAPI } from "../api/admin";
import type {
  CreateNumberRequestData,
  NumberRequestState,
  UpdateNumberRequestData,
} from "../../number-requests/types";
import type {
  AdminGeoFilter,
  AdminHandsetLimitsInput,
  AdminNumberRequest,
  ControlWindow,
  CreditProvider,
  ProvisionInput,
  SessionSort,
  StaffAccess,
  ToolErrorFilters,
} from "../types";

export const adminKeys = {
  all: ["admin"] as const,
  tenants: (days: number, id?: string) =>
    [...adminKeys.all, "tenants", days, id ?? null] as const,
  tenantUsers: (tenantId: string) =>
    [...adminKeys.all, "tenant-users", tenantId] as const,
  staffAccounts: () => [...adminKeys.all, "staff-accounts"] as const,
  dashboard: (days: number) => [...adminKeys.all, "dashboard", days] as const,
  controlCenter: (span: ControlWindow) =>
    [...adminKeys.all, "control-center", span] as const,
  technical: (days: number) => [...adminKeys.all, "technical", days] as const,
  costs: (days: number) => [...adminKeys.all, "costs", days] as const,
  timeseries: (hours: number) =>
    [...adminKeys.all, "timeseries", hours] as const,
  sessions: (opts: unknown) => [...adminKeys.all, "sessions", opts] as const,
  credits: () => [...adminKeys.all, "credits"] as const,
  handsetLimits: () => [...adminKeys.all, "telephony", "handsets"] as const,
  connectorGeoFilters: () =>
    [...adminKeys.all, "telephony", "connectors"] as const,
  contextPressure: (opts: unknown) =>
    [...adminKeys.all, "context-pressure", opts] as const,
  externalCalls: (opts: unknown) =>
    [...adminKeys.all, "external-calls", opts] as const,
  mediaHealth: (opts: unknown) =>
    [...adminKeys.all, "media-health", opts] as const,
  toolErrors: (opts: unknown) =>
    [...adminKeys.all, "tool-errors", opts] as const,
  numberRequests: (filters: unknown) =>
    [...adminKeys.all, "number-requests", "list", filters] as const,
  numberRequest: (clientId: string, requestId: string) =>
    [...adminKeys.all, "number-requests", clientId, requestId] as const,
  syntheticCalls: (limit: number) =>
    [...adminKeys.all, "synthetic-calls", "list", limit] as const,
  syntheticCall: (id: string) =>
    [...adminKeys.all, "synthetic-calls", id] as const,
};

/**
 * Credit state of the upstream vendors. The server keeps its probes for 10 minutes, so
 * refetching on focus is cheap; the alerting cron is what actually watches these, the panel
 * only shows where things stand.
 */
export function useAdminCredits() {
  return useQuery({
    queryKey: adminKeys.credits(),
    queryFn: () => AdminAPI.getCredits(),
    staleTime: 5 * 60 * 1000,
  });
}

/** Force a fresh probe of every vendor (panel "Rafraîchir"). */
export function useRefreshCredits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => AdminAPI.getCredits(true),
    onSuccess: (credits) => {
      queryClient.setQueryData(adminKeys.credits(), credits);
    },
  });
}

/** Manual balance / per-provider threshold. */
export function useUpdateProviderCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      provider,
      ...input
    }: {
      provider: CreditProvider;
      balance?: number | null;
      threshold?: number | null;
    }) => AdminAPI.updateCredit(provider, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.credits() });
    },
  });
}

/** Run the alerting pass now — verifies the whole chain down to Sentry. */
export function useCheckCredits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => AdminAPI.checkCredits(),
    onSuccess: (credits) => {
      queryClient.setQueryData(adminKeys.credits(), credits);
    },
  });
}

/** The tenants listed (no demo space) — or, with `id`, that one tenant, demo space or not. */
export function useAdminTenants(days = 30, id?: string) {
  return useQuery({
    queryKey: adminKeys.tenants(days, id),
    queryFn: () => AdminAPI.getTenants(days, id),
    staleTime: 60 * 1000,
  });
}

export function useAdminDashboard(days = 30) {
  return useQuery({
    queryKey: adminKeys.dashboard(days),
    queryFn: () => AdminAPI.getDashboard(days),
    staleTime: 60 * 1000,
  });
}

/**
 * The control centre, polled: it is a screen left open on a wall, so it refreshes on its
 * own (every 10s, paused while the tab is hidden) and keeps the last payload on screen
 * while the next one loads — a refresh must never blank the page.
 */
export function useAdminControlCenter(span: ControlWindow = 24, live = true) {
  return useQuery({
    queryKey: adminKeys.controlCenter(span),
    queryFn: () => AdminAPI.getControlCenter(span),
    refetchInterval: live ? 10 * 1000 : false,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
    staleTime: 5 * 1000,
  });
}

/** The usage ledger priced with the config price list (GET /costs). */
export function useAdminCosts(days = 30) {
  return useQuery({
    queryKey: adminKeys.costs(days),
    queryFn: () => AdminAPI.getCosts(days),
    staleTime: 60 * 1000,
  });
}

export function useAdminTechnical(days = 30) {
  return useQuery({
    queryKey: adminKeys.technical(days),
    queryFn: () => AdminAPI.getTechnical(days),
    staleTime: 60 * 1000,
  });
}

/** Hourly technical series — written by the backend cron, refreshed every hour. */
export function useAdminTimeseries(hours = 24 * 7) {
  return useQuery({
    queryKey: adminKeys.timeseries(hours),
    queryFn: () => AdminAPI.getTimeseries(hours),
    staleTime: 5 * 60 * 1000,
  });
}

/** Recompute the hourly series (seed history on first use, or repair a window). */
export function useBackfillTimeseries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hours: number) => AdminAPI.backfillTimeseries(hours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

/** Members of a tenant (SYSADMIN provisioning screen). */
export function useAdminTenantUsers(tenantId: string) {
  return useQuery({
    queryKey: adminKeys.tenantUsers(tenantId),
    queryFn: () => AdminAPI.getTenantUsers(tenantId),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
}

/** Who reaches the back-office: administrators and read-only accounts (sales). */
export function useStaffAccounts() {
  return useQuery({
    queryKey: adminKeys.staffAccounts(),
    queryFn: () => AdminAPI.getStaffAccounts(),
    staleTime: 30 * 1000,
  });
}

/** Give or take away an account's back-office access (admin, read, none). */
export function useSetStaffAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      email,
      access,
    }: {
      email: string;
      access: StaffAccess | "none";
    }) => AdminAPI.setStaffAccess(email, access),
    onSuccess: (accounts) => {
      queryClient.setQueryData(adminKeys.staffAccounts(), accounts);
    },
  });
}

/** Create a user with chosen credentials, in a new or existing tenant. */
export function useProvisionUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ProvisionInput) => AdminAPI.provision(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ userId, password }: { userId: string; password?: string }) =>
      AdminAPI.resetPassword(userId, password),
  });
}

/** Revoke a member's access to a tenant (SYSADMIN provisioning screen). */
export function useRemoveTenantUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, userId }: { tenantId: string; userId: string }) =>
      AdminAPI.removeTenantUser(tenantId, userId),
    onSuccess: (_data, { tenantId }) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.tenantUsers(tenantId),
      });
    },
  });
}

export function useAdminSessions(opts: {
  days?: number;
  sort?: SessionSort;
  order?: "asc" | "desc";
  fallbackUsed?: boolean;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.sessions(opts),
    queryFn: () => AdminAPI.getSessions(opts),
    staleTime: 60 * 1000,
  });
}

// ----------------------------------------------------------- context pressure

/** How full the talking models' context window gets, estimated per turn. */
export function useContextPressure(hours: number) {
  return useQuery({
    queryKey: adminKeys.contextPressure([hours]),
    queryFn: () => AdminAPI.getContextPressure(hours),
    staleTime: 60 * 1000,
  });
}

// ----------------------------------------------------------- media health

/** Per-call media verdicts, written when each phone leg ends — refetched like a live page. */
export function useMediaHealth(hours: number, all: boolean) {
  return useQuery({
    queryKey: adminKeys.mediaHealth([hours, all]),
    queryFn: () => AdminAPI.getMediaHealth(hours, all),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

// ----------------------------------------------------------- external calls

/**
 * Outbound third-party traffic (booking adapters, tenants' custom APIs, MCP servers,
 * browser domains). Refetched on an interval: unlike the hourly snapshots this is computed
 * live from the raw rows, so an incident shows up within seconds of happening.
 */
export function useExternalCalls(
  hours: number,
  filters: { group?: string } = {},
) {
  return useQuery({
    queryKey: adminKeys.externalCalls(["overview", hours, filters]),
    queryFn: () => AdminAPI.getExternalCalls(hours, filters),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/** Per-endpoint rows inside one integration. Disabled until a group is selected. */
export function useExternalCallOperations(
  hours: number,
  filters: { group?: string; target?: string },
) {
  return useQuery({
    queryKey: adminKeys.externalCalls(["operations", hours, filters]),
    queryFn: () => AdminAPI.getExternalCallOperations(hours, filters),
    // Not gated on a group: the cross-integration list is how one finds which endpoint
    // all the traffic goes to before knowing which integration to open.
    staleTime: 30 * 1000,
  });
}

export function useExternalCallsTimeseries(
  hours: number,
  filters: { group?: string; target?: string } = {},
) {
  return useQuery({
    queryKey: adminKeys.externalCalls(["timeseries", hours, filters]),
    queryFn: () => AdminAPI.getExternalCallsTimeseries(hours, filters),
    staleTime: 30 * 1000,
  });
}

export function useExternalCallFailures(
  hours: number,
  filters: { group?: string; target?: string; limit?: number } = {},
) {
  return useQuery({
    queryKey: adminKeys.externalCalls(["failures", hours, filters]),
    queryFn: () => AdminAPI.getExternalCallFailures(hours, filters),
    staleTime: 30 * 1000,
  });
}

// -------------------------------------------------------------- tool errors

/** Per-tool rollup of failed tool calls. Computed live from `session_messages`. */
export function useToolErrors(hours: number, filters: ToolErrorFilters = {}) {
  return useQuery({
    queryKey: adminKeys.toolErrors(["overview", hours, filters]),
    queryFn: () => AdminAPI.getToolErrors(hours, filters),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/** One page of failed tool calls, newest first. */
export function useToolErrorList(
  hours: number,
  filters: ToolErrorFilters = {},
  page: { limit: number; offset: number },
) {
  return useQuery({
    queryKey: adminKeys.toolErrors(["list", hours, filters, page]),
    queryFn: () => AdminAPI.listToolErrors(hours, filters, page),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------- number requests

/**
 * The carrier paperwork of every tenant, in one queue.
 *
 * Long-lived rows that move a few times a week at most, so nothing here polls:
 * what changes them is someone on this very page pressing a button, and the
 * mutations below invalidate what they touched.
 */
export function useAdminNumberRequests(
  filters: { states?: NumberRequestState[]; client?: string } = {},
) {
  return useQuery({
    queryKey: adminKeys.numberRequests(filters),
    queryFn: () => AdminAPI.getNumberRequests(filters),
    staleTime: 60 * 1000,
  });
}

export function useAdminNumberRequest(clientId: string, requestId: string) {
  return useQuery({
    queryKey: adminKeys.numberRequest(clientId, requestId),
    queryFn: () => AdminAPI.getNumberRequest(clientId, requestId),
    enabled: !!clientId && !!requestId,
  });
}

/**
 * Both mutations write the returned row straight into the detail cache and drop
 * every cached list: a transition changes the ordering key of the queue, so the
 * list has to be recomputed server-side rather than patched here.
 */
const useNumberRequestMutation = <
  TVariables,
  TResult extends AdminNumberRequest = AdminNumberRequest,
>(
  clientId: string,
  requestId: string,
  mutationFn: (variables: TVariables) => Promise<TResult>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (request) => {
      queryClient.setQueryData(
        adminKeys.numberRequest(clientId, requestId),
        request,
      );
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "number-requests"],
      });
    },
  });
};

/** Advance the approval state machine (illegal moves are refused server-side). */
export function useTransitionNumberRequest(
  clientId: string,
  requestId: string,
) {
  return useNumberRequestMutation(
    clientId,
    requestId,
    ({ state, comment }: { state: NumberRequestState; comment?: string }) =>
      AdminAPI.transitionNumberRequest(clientId, requestId, state, comment),
  );
}

/** Edit the request on the tenant's behalf — in practice, the number we got. */
export function useUpdateAdminNumberRequest(
  clientId: string,
  requestId: string,
) {
  return useNumberRequestMutation(
    clientId,
    requestId,
    (data: UpdateNumberRequestData) =>
      AdminAPI.updateNumberRequest(clientId, requestId, data),
  );
}

/** Add a parcel — which e-mails the client its tracking link. */
export function useAddNumberRequestDelivery(
  clientId: string,
  requestId: string,
) {
  return useNumberRequestMutation(
    clientId,
    requestId,
    (data: { tracking_url: string; label?: string }) =>
      AdminAPI.addNumberRequestDelivery(clientId, requestId, data),
  );
}

/** Remove a parcel (a wrong link, a cancelled shipment). */
export function useRemoveNumberRequestDelivery(
  clientId: string,
  requestId: string,
) {
  return useNumberRequestMutation(clientId, requestId, (deliveryId: string) =>
    AdminAPI.removeNumberRequestDelivery(clientId, requestId, deliveryId),
  );
}

/** Open a request for a tenant from the back-office. */
export function useCreateAdminNumberRequest(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNumberRequestData) =>
      AdminAPI.createNumberRequest(clientId, data),
    onSuccess: (request) => {
      queryClient.setQueryData(
        adminKeys.numberRequest(clientId, request.id),
        request,
      );
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "number-requests"],
      });
    },
  });
}

/**
 * Validate a file: record the number, wire up the HeyUp channel, put the number
 * on our SIP trunk in the trunk's region, mark the request provisioned. Invalidates the connector caches too — the tenant now
 * has a channel it did not have a moment ago.
 */
export function useProvisionNumberRequest(clientId: string, requestId: string) {
  const queryClient = useQueryClient();
  const mutation = useNumberRequestMutation(
    clientId,
    requestId,
    (data: { phone_number?: string; comment?: string }) =>
      AdminAPI.provisionNumberRequest(clientId, requestId, data),
  );

  return {
    ...mutation,
    mutateAsync: async (data: { phone_number?: string; comment?: string }) => {
      const request = await mutation.mutateAsync(data);
      queryClient.invalidateQueries({ queryKey: ["sessions_connectors"] });
      return request;
    },
  };
}

// ------------------------------------------------------------ synthetic calls

/**
 * History of the daily end-to-end booking test.
 *
 * One row a day: the cron places the call before opening hours, so the list normally does
 * not move and is not polled. The exception is a call in flight — started from this page or
 * by the cron on another instance — which the server reports as `running`; the row only
 * appears when the call is over, so we poll while that lasts.
 */
export function useSyntheticCalls(limit = 30) {
  return useQuery({
    queryKey: adminKeys.syntheticCalls(limit),
    queryFn: () => AdminAPI.getSyntheticCalls({ limit }),
    staleTime: 60 * 1000,
    refetchInterval: (query) => (query.state.data?.running ? 10000 : false),
  });
}

/** One run with its transcript — loaded only when a row is opened. */
export function useSyntheticCall(id: string | null) {
  return useQuery({
    queryKey: adminKeys.syntheticCall(id || ""),
    queryFn: () => AdminAPI.getSyntheticCall(id || ""),
    enabled: !!id,
    staleTime: Infinity, // a finished run never changes
  });
}

/**
 * Start the test call now.
 *
 * Resolves as soon as the call is placed, not when it ends: the list query then polls
 * (see `useSyntheticCalls`) until the finished run shows up, a few minutes later.
 */
export function useRunSyntheticCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => AdminAPI.runSyntheticCall(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "synthetic-calls"],
      });
    },
  });
}

// ── Telephony limits ─────────────────────────────────────────────────────────
//
// What a tenant's telephony can cost us: outbound authorisation and the two
// ceilings on a handset, and the countries a connector accepts calls from. Read
// and written cross-tenant, because platform staff are not members of the
// tenants they configure.

export function useAdminHandsetLimits() {
  return useQuery({
    queryKey: adminKeys.handsetLimits(),
    queryFn: AdminAPI.getHandsetLimits,
    staleTime: 30 * 1000,
  });
}

export function useSetHandsetLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      deviceId,
      ...input
    }: AdminHandsetLimitsInput & { clientId: string; deviceId: string }) =>
      AdminAPI.setHandsetLimits(clientId, deviceId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.handsetLimits() });
    },
  });
}

export function useAdminConnectorGeoFilters() {
  return useQuery({
    queryKey: adminKeys.connectorGeoFilters(),
    queryFn: AdminAPI.getConnectorGeoFilters,
    staleTime: 30 * 1000,
  });
}

export function useSetConnectorGeoFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      connectorId,
      geoFilter,
    }: {
      clientId: string;
      connectorId: string;
      geoFilter: AdminGeoFilter;
    }) => AdminAPI.setConnectorGeoFilter(clientId, connectorId, geoFilter),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.connectorGeoFilters(),
      });
    },
  });
}

// ------------------------------------------------------------------ demo spaces

/**
 * The imported salons. Each takes a minute or two to build, so the list is polled
 * for as long as any of them is queued or running, and left alone after.
 */
export function useDemoSpaces() {
  return useQuery({
    queryKey: [...adminKeys.all, "demo-spaces"],
    queryFn: () => AdminAPI.getDemoSpaces(),
    staleTime: 10 * 1000,
    refetchInterval: (query) =>
      query.state.data?.spaces.some(
        (space) => space.status === "pending" || space.status === "running",
      )
        ? 5000
        : false,
  });
}

export function useImportDemoSpaces() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (csv: string) => AdminAPI.importDemoSpaces(csv),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "demo-spaces"],
      }),
  });
}

export function useRetryDemoSpace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => AdminAPI.retryDemoSpace(id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "demo-spaces"],
      }),
  });
}
